// TownMap.js
import React, { useEffect, useRef, useState } from "react";
import { Container } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";

import { withTranslation } from "react-i18next";

import ExpandingCircleMenu from '../components/ExpandingCircleMenu';
import BreadcrumbAboveImage from "components/Common/BreadcrumbAboveImage";

import townMap from "../../../assets/images/info/townMap.png";
import mapPin from "../../../assets/images/info/map-pin.png";
import mapPinGrey from "../../../assets/images/info/map-pin-grey.png";
import MapPin from "./MapPin";
import MapPinGrey from "./MapPinGrey";

import "./TownMap.css";

const TownMap = ({ t }) => {
  const containerRef = useRef(null);
  const divRef = useRef(null);
  const imageRef = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [rect, setRect] = useState({ width: 0, height: 0 });
  const [imgSize, setImgSize] = useState({ width: 1350, height: 900 }); // default fallback
  const [dragStart, setDragStart] = useState(null);
  const [isDragging, setIsDragging] = useState(false);  // for UI
  const isDraggingRef = useRef(false);                  // for logic

  const navigate = useNavigate();


  //---------------------------------
  // Resize and SideBar expand
  //---------------------------------

  const handleResize = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();

    const minX = rect.width - imgSize.width;
    const minY = rect.height - imgSize.height;
    setRect(prev => ({
      width: rect.width,
      height: rect.height
    }))
    setPos(prev => ({
      x: Math.min(0, Math.max(minX, prev.x)),
      y: Math.min(0, Math.max(minY, prev.y))
    }));
  };

  const [isSideBarExpanded, setIsSideBarExpanded] = useState(true);
  useEffect(() => {
    const checkWidth = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const ninetyVW = window.innerWidth * 0.9;
      console.log('container width:', rect.width);
      console.log('90vw:', ninetyVW);
      if (rect.width > ninetyVW) {
        console.log('Container is wider than 90vw');
        setIsSideBarExpanded(false);
      } else {
        console.log('Container is narrower than 90vw');
        setIsSideBarExpanded(true);
      }
    };

    const observer = new ResizeObserver(() => {
      checkWidth();
      handleResize();
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Also run once on mount
    checkWidth();

    return () => {
      observer.disconnect();
    };
  }, []);

  // Handle window resize and clamp
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [imgSize]);







  // Handle mouse movement
  useEffect(() => {
    const edgeThreshold = 30;
    const speed = 10;

    const pan = (e) => {
      if (!containerRef.current || !divRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const { x: containerX, y: containerY, width, height } = rect;

      // Only pan if mouse is inside container
      if (
        e.clientX < containerX || e.clientX > containerX + width ||
        e.clientY < containerY || e.clientY > containerY + height
      ) return;

      let dx = 0, dy = 0;

      //console.log('e.clientX:', e.clientX); //Left<->Right
      //console.log('e.clientY:', e.clientY); //Up<->Down

      if (e.clientX <= containerX + edgeThreshold) dx = speed;         // Move left
      else if (e.clientX >= containerX + width - edgeThreshold) dx = -speed; // Move right

      if (e.clientY <= containerY + edgeThreshold) dy = speed;         // Move up
      else if (e.clientY >= containerY + height - edgeThreshold) dy = -speed; // Move down

      const newX = pos.x + dx;
      const newY = pos.y + dy;

      // Clamp movement to image bounds (1350 x 900 assumed)
      if (newX > 0 || newX < width - 1350) dx = 0;
      if (newY > 0 || newY < height - 900) dy = 0;

      // Not in use
      if(!isDragging && isDragging){
      /*if(!isDragging){*/
        if (dx !== 0 || dy !== 0) {
          setPos(prev => ({ x: prev.x + dx, y: prev.y + dy }));
        }
      }

    };

    const debouncedPan = debounce(pan, 1, {
      leading: true,   // run immediately on first event
      trailing: false, // then ignore until 50ms elapses
    });

    window.addEventListener("mousemove", debouncedPan);
    return () => {
      window.removeEventListener("mousemove", debouncedPan);
      debouncedPan.cancel(); // clean up any pending
    };
  }, [pos, imgSize, isDragging]);

  // Touch support
  useEffect(() => {
    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      setDragStart({ x: touch.clientX, y: touch.clientY, origin: { ...pos } });
    };

    const handleTouchMove = (e) => {
      if (!dragStart || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      const dx = touch.clientX - dragStart.x;
      const dy = touch.clientY - dragStart.y;

      const minX = rect.width - imgSize.width;
      const minY = rect.height - imgSize.height;

      let newX = dragStart.origin.x + dx;
      let newY = dragStart.origin.y + dy;

      newX = Math.min(0, Math.max(minX, newX));
      newY = Math.min(0, Math.max(minY, newY));

      setPos({ x: newX, y: newY });
    };

    const handleTouchEnd = () => {
      setDragStart(null);
    };

    const node = containerRef.current;
    if (!node) return;

    node.addEventListener("touchstart", handleTouchStart);
    node.addEventListener("touchmove", handleTouchMove);
    node.addEventListener("touchend", handleTouchEnd);

    return () => {
      node.removeEventListener("touchstart", handleTouchStart);
      node.removeEventListener("touchmove", handleTouchMove);
      node.removeEventListener("touchend", handleTouchEnd);
    };
  }, [dragStart, imgSize, pos]);

  const startX = useRef(0);
  const startY = useRef(0);
  const originX = useRef(0);
  const originY = useRef(0);

  useEffect(() => {

    const handleMouseDown = (e) => {
      isDraggingRef.current = true;
      setIsDragging(true);
      startX.current = e.clientX;
      startY.current = e.clientY;
      //console.log('startX, startY:', startX, startY);
      originX.current = pos.x;
      originY.current = pos.y;
    };

    const handleMouseMove = (e) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = e.clientX - startX.current;
      const dy = e.clientY - startY.current;

      const minX = rect.width - imgSize.width;
      const minY = rect.height - imgSize.height;

      //console.log('dx, dy:', dx, dy);
      //console.log('startX, startY:', startX.current, startY.current);

      let newX = originX.current + dx;
      let newY = originY.current + dy;

      newX = Math.min(0, Math.max(minX, newX));
      newY = Math.min(0, Math.max(minY, newY));

      setPos({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      setIsDragging(false);
    };

    const node = containerRef.current;
    if (!node) return;

    node.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      node.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [pos, imgSize]);

  useEffect(()=>{
    console.log('rect.height:', rect.height);
    console.log('rect.width:', rect.width);
  },[rect.height, rect.width])

  /*
  useEffect(()=>{
    const rectC = containerRef.current.getBoundingClientRect();
    console.log('containerRef.current.style.width changed:', rectC);
  },[containerRef.current])
  */
  

  return (
    <div className="page-content" style={{ 
      maxHeight: "100vh",
      padding: "70px 0px 0px 0px"
    }}>
      <ExpandingCircleMenu />
      <div ref={containerRef}>
        <Container
          fluid
          style={{ 
            minHeight: "calc(98vh)",
            maxHeight: "calc(98vh)",
            overflow: "hidden",
            //border: "3px solid black",
            padding: "0"
          }}
        >
          <div 
            style={{
              paddingTop: "20px",
              paddingLeft: "30px",
              paddingRight: "30px",
              position: "relative",
              marginBottom: "-120px",
              zIndex: "10"
            }}
          >
            <BreadcrumbAboveImage
              title={t("My Info")}
              breadcrumbItems={[
                { title: t("我的 AI 應用主頁"), to: "/myInfo/dashboard" }
              ]}
            />
          </div>

          <div  
            ref={divRef}
            style={{
              position: "relative",
              top:  `${pos.y}px`,
              left: `${pos.x}px`,
              width: `${imgSize.width}px`,
              /*height: `${imgSize.height}px`,*/
              minWidth: "100vw",
              //transition: "top 0.03s linear, left 0.03s linear",
              touchAction: "none",
              overflow: "hidden",
              zIndex: "1",
              userSelect: "none"
            }}
          >
            {/* Town Hall */}
            <div style={{ position: "absolute", zIndex: "10", 
                top: `calc(36% + ${Math.max(0,rect.width-80-(isSideBarExpanded ? 1180 : 1350))*(isSideBarExpanded ? 1/5 : 1/3.5)}px)`,
                left: `calc(59.5% + ${Math.max(0,rect.width-1100)*1/70}px)`,
              }}
            >
              <MapPinGrey
                style={{ height: "50px" }}
                imgSrc={mapPinGrey}
              />
              {/*
                <MapPin
                  style={{ height: "50px" }}
                  imgSrc={mapPin}
                  onClick={() => navigate("/myInfo/town-hall")}
                />
              */}
            </div>
            {/* Board */}
            <div style={{ position: "absolute", zIndex: "10", 
                top: `calc(63% + ${Math.max(0,rect.width-80-(isSideBarExpanded ? 1180 : 1350))*(isSideBarExpanded ? 1/2 : 1/1.1)}px)`,
                left: `calc(50% + ${Math.max(0,rect.width-1100)*1/70}px)`,
              }}
            >
              <MapPin
                style={{ height: "50px" }}
                imgSrc={mapPin}
                onClick={() => navigate("/myInfo/discover")}
              />
            </div>
            {/* Home */}
            <div 
              style={{ position: "absolute", zIndex: "10", 
                top: `calc(42% + ${Math.max(0,rect.width-80-(isSideBarExpanded ? 1180 : 1350))*(isSideBarExpanded ? 1/2.8 : 1/1.6)}px)`,
                left: `calc(19% + ${Math.max(0,rect.width-1100)*1/70}px)` 
              }}
            >
              <MapPin
                style={{ height: "50px" }}
                imgSrc={mapPin}
                onClick={() => navigate("/myInfo/navigateThemes")}
              />
            </div>
            {/* Bank */}
            <div style={{ position: "absolute", zIndex: "10", 
                top: `calc(69% + ${Math.max(0,rect.width-80-(isSideBarExpanded ? 1180 : 1350))*(isSideBarExpanded ? 1/2.8 : 1/1.6)}px)`,
                left: `calc(81% + ${Math.max(0,rect.width-1100)*1/70}px)` 
              }}
            >
              <MapPinGrey
                style={{ height: "50px" }}
                imgSrc={mapPinGrey}
              />
              {/*
              <MapPin
                style={{ height: "50px" }}
                imgSrc={mapPin}
                onClick={() => navigate("/myInfo/pointsBank")}
              />
              */}
            </div>



            <img
              ref={imageRef}
              src={townMap}
              alt="Town Map"
              style={{
                //position: "absolute",
                minHeight: "calc(-20px + 100vh)",
                //objectFit: "cover",
                //cursor: (dragStart || isDragging) ? "grabbing" : "grab",
                transition:"top 0.05s linear,left 0.05s linear",
                userSelect: "none",
                pointerEvents: "none"
              }}
            />
          </div>
        </Container>
      </div>
    </div>
  );
};

export default withTranslation()(TownMap);
